import { useEffect, useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { FileDown } from "lucide-react";
import { useSeo } from "@/hooks/useSeo";
import { AdminNav } from "@/components/AdminNav";
import { GUIDES, guideById } from "@/data/guides";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const inputCls =
  "w-full bg-transparent border-b border-ink/25 py-3 text-base text-ink outline-none placeholder:text-soft/50 focus:border-clay transition-colors duration-300";

const fmtWhen = (iso) => {
  if (!iso) return "—";
  return String(iso).replace("T", " ").slice(0, 16) + " UTC";
};

export default function AdminGuides() {
  useSeo({ title: "Guide requests", noindex: true });
  const [token, setToken] = useState(localStorage.getItem("wd-admin") || "");
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);

  const load = async (t) => {
    try {
      const { data } = await axios.get(`${API}/admin/guide-requests`, { headers: { "X-Admin-Token": t } });
      setData(data);
      setError(null);
    } catch {
      setData(null);
      setError("That key didn't work — check it and try again.");
    }
  };

  useEffect(() => {
    if (token) load(token);
  }, []);

  const saveToken = (t) => {
    setToken(t);
    localStorage.setItem("wd-admin", t);
    setError(null);
    if (t) load(t);
  };

  return (
    <main className="bg-cream min-h-screen" data-testid="admin-guides-page">
      <section className="mx-auto max-w-3xl px-6 pt-40 pb-24">
        <div className="flex items-center gap-3 text-clay">
          <FileDown size={20} strokeWidth={1.25} />
          <p className="text-xs tracking-[0.35em] uppercase">Free guides</p>
        </div>
        <h1 className="mt-6 font-serif text-4xl sm:text-5xl tracking-tight text-ink">Guide requests</h1>
        <p className="mt-4 text-base leading-relaxed text-soft">
          {data === null
            ? "Enter your key to see who asked for which guide."
            : `${data.total} request${data.total === 1 ? "" : "s"} so far. The Slow Letter column shows who also joined the letter.`}
        </p>

        <div className="mt-8">
          <AdminNav current="/admin/guides" />
        </div>

        <div className="mt-10 max-w-xs">
          <label htmlFor="adm-token" className="text-xs tracking-[0.25em] uppercase text-soft">Your key</label>
          <input id="adm-token" type="password" value={token} onChange={(e) => saveToken(e.target.value)}
            placeholder="Admin key" className={inputCls} data-testid="guides-token-input" />
          {error && <p className="mt-2 text-sm text-rust" data-testid="guides-error">{error}</p>}
        </div>

        {data !== null && (
          <div className="mt-10 flex flex-wrap gap-2" data-testid="guides-counts">
            {GUIDES.map((g) => (
              <span key={g.id} data-testid={`guide-count-${g.id}`}
                className="rounded-full border border-ink/15 px-4 py-1.5 text-xs tracking-[0.1em] uppercase text-soft">
                {g.title.en} · <strong className="font-medium text-ink">{data.counts[g.id] || 0}</strong>
              </span>
            ))}
          </div>
        )}

        {data !== null && data.requests.length === 0 && (
          <p className="mt-12 font-serif italic text-lg text-soft" data-testid="guides-empty">No guide requests yet.</p>
        )}

        {data !== null && data.requests.length > 0 && (
          <ul className="mt-12 divide-y divide-ink/8 border-t border-ink/15" data-testid="guides-list">
            {data.requests.map((r, i) => (
              <motion.li key={r.id || i} initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}
                className="flex flex-wrap items-center gap-x-6 gap-y-1 py-4" data-testid={`guide-request-row-${i}`}>
                <div className="min-w-[180px] flex-1">
                  <p className="text-base text-ink">{r.email}</p>
                  <p className="text-sm text-soft">{fmtWhen(r.created_at)}</p>
                </div>
                <p className="text-sm text-soft">{(guideById(r.guide) || { title: { en: r.guide } }).title.en}</p>
                <p className="text-xs tracking-[0.15em] uppercase text-soft">{r.lang}</p>
                <span
                  className={`inline-block rounded-full border px-2.5 py-0.5 text-[11px] tracking-[0.12em] uppercase ${
                    r.slow_letter ? "border-sage/40 bg-sage/10 text-sage" : "border-ink/15 text-soft/60"
                  }`}
                  data-testid={`guide-request-letter-${i}`}
                >
                  {r.slow_letter ? "Slow Letter" : "Guide only"}
                </span>
              </motion.li>
            ))}
          </ul>
        )}
      </section>
    </main>
  );
}
